import React from 'react';
import { Empty, type TableProps } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppButton, AppPagination, AppTable } from '@/components/atoms';
import { EditFill, EllipsisH } from '@/components/atoms/AppIcon';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { DistanceCacheListItem } from '@/services/admin/distance-caches';
import { EMPTY_TEXT_FILTERED } from '@/constants';

type DistanceCachesMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type DistanceCachesTableSectionProps = {
  rows: DistanceCacheListItem[];
  loading: boolean;
  meta: DistanceCachesMeta;
  defaultPerPage: number;
  /** Show サイト区分 column for super-admin or family admin */
  canUseSiteTypeFeatures?: boolean;
  tenantNameBySiteType?: Map<string, string>;
  sortInfo: {
    columnKey?: string;
    order?: 'ascend' | 'descend';
  };
  onSort: (
    sorter: SorterResult<DistanceCacheListItem> | SorterResult<DistanceCacheListItem>[]
  ) => void;
  onChangePage: (page: number) => void;
};

const formatDecimalValue = (value: string | number | null | undefined): string => {
  if (value == null || value === '') return '';
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(8) : String(value);
};

const DistanceCachesTableSection: React.FC<DistanceCachesTableSectionProps> = ({
  rows,
  loading,
  meta,
  defaultPerPage,
  canUseSiteTypeFeatures = false,
  tenantNameBySiteType = new Map(),
  sortInfo,
  onSort,
  onChangePage,
}) => {
  const { colors } = useTheme();
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'super-admin';
  const navigate = useNavigate();
  const location = useLocation();

  const columns: TableProps<DistanceCacheListItem>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'id' ? sortInfo.order : undefined,
      width: 80,
    },
    {
      title: '治療院ID',
      dataIndex: 'clinic_number',
      key: 'clinic_number',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'clinic_number' ? sortInfo.order : undefined,
      width: 80,
    },
    {
      title: 'サイト区分',
      dataIndex: 'site_type',
      key: 'site_type',
      hidden: !canUseSiteTypeFeatures,
      render: (siteType: string | null) => {
        if (!siteType) return '';
        return tenantNameBySiteType.get(siteType) ?? siteType;
      },
      width: 120,
    },
    {
      title: '距離',
      dataIndex: 'distance',
      key: 'distance',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'distance' ? sortInfo.order : undefined,
      width: 80,
    },
    {
      title: 'From住所',
      dataIndex: 'address1',
      key: 'address1',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'address1' ? sortInfo.order : undefined,
      width: 150,
    },
    {
      title: 'From緯度',
      dataIndex: 'latitude1',
      key: 'latitude1',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'latitude1' ? sortInfo.order : undefined,
      width: 100,
      render: formatDecimalValue,
    },
    {
      title: 'From経度',
      dataIndex: 'longitude1',
      key: 'longitude1',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'longitude1' ? sortInfo.order : undefined,
      width: 100,
      render: formatDecimalValue,
    },
    {
      title: 'To住所',
      dataIndex: 'address2',
      key: 'address2',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'address2' ? sortInfo.order : undefined,
      width: 150,
    },
    {
      title: 'To緯度',
      dataIndex: 'latitude2',
      key: 'latitude2',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'latitude2' ? sortInfo.order : undefined,
      width: 100,
      render: formatDecimalValue,
    },
    {
      title: 'To経度',
      dataIndex: 'longitude2',
      key: 'longitude2',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'longitude2' ? sortInfo.order : undefined,
      width: 100,
      render: formatDecimalValue,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <div className="flex flex-col gap-2">
          <AppButton
            icon={<EllipsisH />}
            type="primary"
            className="w-[76px]"
            onClick={() =>
              navigate(`/${currentSite}/admin/distance_caches/${record.id}`, {
                state: { listSearch: location.search },
              })
            }
          >
            表示
          </AppButton>
          <AppButton
            style={
              {
                backgroundColor: colors.secondary,
                ['--action-bg-hover' as string]: colors.secondaryHover,
              } as React.CSSProperties
            }
            className="w-[76px] hover:!bg-[var(--action-bg-hover)]"
            icon={<EditFill />}
            type="primary"
            onClick={() =>
              navigate(`/${currentSite}/admin/distance_caches/${record.id}/edit`, {
                state: { listSearch: location.search },
              })
            }
          >
            編集
          </AppButton>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-md border">
      <div className="overflow-hidden">
        <AppTable
          isAdminSite
          className="w-full min-w-0"
          tableLayout="fixed"
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          onChange={(_pagination, _filters, sorter) =>
            onSort(
              sorter as SorterResult<DistanceCacheListItem> | SorterResult<DistanceCacheListItem>[]
            )
          }
          scroll={{ x: '100%' }}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />
            ),
          }}
        />
      </div>
      {meta && (
        <div className="p-4 flex justify-end">
          <AppPagination
            current={meta.current_page}
            total={meta.total}
            pageSize={meta.per_page || defaultPerPage}
            hideOnSinglePage
            onChange={p => onChangePage(p)}
          />
        </div>
      )}
    </div>
  );
};

export default DistanceCachesTableSection;
